import React, { useEffect, useMemo, useState } from 'react';
import UGCLayout from '@/components/layouts/UGCLayout';
import NavigationBar from '@/components/ui/NavigationBar';
import Button from '@/components/ui/Button';
import Icon from '@/components/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { hasFeatureAccess } from '@/config/featureAccess';
import storageService from '@/services/storageService';
import { ugcMarketplaceService } from '@/services/ugcMarketplaceService';

const navItems = [
  { path: '/app/ugc-dashboard', label: 'Dashboard', icon: 'Layout' },
  { path: '/app/ugc-marketplace', label: 'Marketplace', icon: 'ShoppingBag' },
  { path: '/app/ugc-brands', label: 'Brands', icon: 'Database' },
  { path: '/app/ugc-scripts', label: 'Scripts', icon: 'FileText' },
  { path: '/app/teleprompter', label: 'Teleprompter', icon: 'PlayCircle' },
];

const moreItems = [
  { path: '/app/ugc-hub', label: 'Creator Hub', icon: 'Compass' },
  { path: '/app/ugc-crm', label: 'CRM', icon: 'Users' },
  { path: '/app/ugc-community', label: 'Community', icon: 'MessageCircle' },
  { path: '/app/ugc-media', label: 'Media Library', icon: 'Film' },
];

const defaultDealForm = {
  title: '',
  brief: '',
  deliverables: '',
  budgetMin: '',
  budgetMax: '',
  deadline: ''
};

const MarketplacePage = () => {
  const { profile } = useAuth();
  const { ugcTier } = useSubscription();
  const userTier = ugcTier || profile?.ugc_tier || 'free';
  const canAccess = hasFeatureAccess('app/ugc-dashboard', userTier);

  const [mode, setMode] = useState('creator');
  const [openDeals, setOpenDeals] = useState([]);
  const [myDeals, setMyDeals] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [bidsByDeal, setBidsByDeal] = useState({});
  const [submissionsByDeal, setSubmissionsByDeal] = useState({});
  const [dealForm, setDealForm] = useState(defaultDealForm);
  const [bidForms, setBidForms] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const acceptedDeals = useMemo(() => {
    return myBids.filter((b) => b.status === 'accepted');
  }, [myBids]);

  const loadData = async () => {
    if (!profile?.id) return;
    setLoading(true);
    try {
      const [open, mine, bids] = await Promise.all([
        ugcMarketplaceService.listOpenDeals(),
        ugcMarketplaceService.listMyDeals(profile.id),
        ugcMarketplaceService.listMyBids(profile.id)
      ]);
      setOpenDeals(open);
      setMyDeals(mine);
      setMyBids(bids);
    } catch (err) {
      console.error('[Marketplace] load failed', err);
      setMessage('Could not load marketplace data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  const handleCreateDeal = async (e) => {
    e.preventDefault();
    if (!profile?.id || !dealForm.title.trim()) return;
    setLoading(true);
    try {
      console.log('Creating deal with:', { brandUserId: profile.id, ...dealForm });
      const result = await ugcMarketplaceService.createDeal({
        brandUserId: profile.id,
        ...dealForm
      });
      console.log('Deal created successfully:', result);
      setDealForm(defaultDealForm);
      setMessage('Deal posted. Creators can now bid.');
      loadData();
    } catch (err) {
      console.error('create deal failed - error:', err);
      console.error('error details:', err?.message, err?.code, err?.details);
      setMessage(`Failed to create deal: ${err?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBid = async (dealId) => {
    if (!profile?.id) return;
    const form = bidForms[dealId] || {};
    if (!form.bidAmount) return;
    setLoading(true);
    try {
      await ugcMarketplaceService.createBid(dealId, profile.id, {
        bidAmount: Number(form.bidAmount),
        bidNote: form.bidNote || '',
        portfolioUrl: form.portfolioUrl || ''
      });
      setBidForms({ ...bidForms, [dealId]: {} });
      setMessage('Bid submitted privately to the brand.');
      loadData();
    } catch (err) {
      console.error('bid failed', err);
      setMessage('Failed to submit bid.');
    } finally {
      setLoading(false);
    }
  };

  const loadBidsForDeal = async (dealId) => {
    try {
      const bids = await ugcMarketplaceService.listBidsForDeal(dealId);
      setBidsByDeal((prev) => ({ ...prev, [dealId]: bids }));
    } catch (err) {
      console.error('load bids failed', err);
    }
  };

  const loadSubmissionsForDeal = async (dealId) => {
    try {
      const subs = await ugcMarketplaceService.listSubmissionsForDeal(dealId);
      setSubmissionsByDeal((prev) => ({ ...prev, [dealId]: subs }));
    } catch (err) {
      console.error('load submissions failed', err);
    }
  };

  const handleAcceptBid = async (dealId, bid) => {
    setLoading(true);
    try {
      // Accept bid and create CRM entry
      await ugcMarketplaceService.acceptBid(dealId, bid.id, {
        brandUserId: profile.id,
        creatorUserId: bid.creator_user_id,
        bidAmount: bid.bid_amount
      });
      // Create invoice
      await ugcMarketplaceService.createInvoiceFromBid({
        dealId,
        brandUserId: profile.id,
        creatorUserId: bid.creator_user_id,
        amount: bid.bid_amount,
        dueDate: null,
        payload: { note: 'Manual invoice - pay creator directly', bidId: bid.id }
      });
      setMessage('Bid accepted. Invoice drafted. Creator added to CRM.');
      await loadBidsForDeal(dealId);
      loadData();
    } catch (err) {
      console.error('accept bid failed', err);
      setMessage('Could not accept bid.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitDelivery = async (dealId, bidId, file, note = '') => {
    if (!file || !profile?.id) return;
    setLoading(true);
    try {
      const upload = await storageService.uploadUGCRecording(file, profile.id, file.name);
      await ugcMarketplaceService.submitDelivery(dealId, bidId, profile.id, {
        storagePath: upload?.path,
        notes: note
      });
      setMessage('Submission uploaded.');
      loadSubmissionsForDeal(dealId);
    } catch (err) {
      console.error('submit delivery failed', err);
      setMessage(`Could not submit delivery: ${err?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  if (!canAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 text-muted-foreground">
        Upgrade your plan to access the marketplace.
      </div>
    );
  }

  return (
    <UGCLayout>
      <NavigationBar items={navItems} moreItems={moreItems} hideLocks />
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 text-amber-100 text-xs font-semibold border border-amber-500/30 mb-3">
              <Icon name="ShoppingBag" size={14} /> UGC Marketplace
            </div>
            <h1 className="text-3xl font-bold text-white">Deals, bids, and deliveries</h1>
            <p className="text-amber-100/80">Brands post deals; creators bid privately; deliveries tracked with manual invoices.</p>
          </div>
          <div className="flex gap-2">
            <Button variant={mode === 'creator' ? 'default' : 'outline'} onClick={() => setMode('creator')}>
              Creator view
            </Button>
            <Button variant={mode === 'brand' ? 'default' : 'outline'} onClick={() => setMode('brand')}>
              Brand view
            </Button>
          </div>
        </div>

        {message && (
          <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-50 px-4 py-3 text-sm">
            {message}
          </div>
        )}

        {mode === 'creator' && (
          <div className="space-y-6">
            <section className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xl font-semibold text-white">Available deals</h2>
                <Button size="sm" variant="ghost" onClick={loadData} disabled={loading}>
                  Refresh
                </Button>
              </div>
              {openDeals.length === 0 && <p className="text-sm text-amber-100/70">No open deals yet. Check back soon.</p>}
              <div className="grid gap-3 md:grid-cols-2">
                {openDeals.map((deal) => {
                  const form = bidForms[deal.id] || {};
                  return (
                    <div key={deal.id} className="rounded-lg border border-white/10 bg-white/5 p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-semibold">{deal.title}</p>
                          <p className="text-xs text-amber-100/70">Deadline: {deal.deadline || 'N/A'}</p>
                        </div>
                        <span className="text-xs px-2 py-1 rounded-full bg-amber-500/20 text-amber-100 border border-amber-500/30">
                          {deal.budget_min ? `$${deal.budget_min}` : '?'} - {deal.budget_max ? `$${deal.budget_max}` : '?' }
                        </span>
                      </div>
                      <p className="text-sm text-amber-100/80 line-clamp-3">{deal.brief || 'No brief provided.'}</p>
                      <div className="space-y-2">
                        <input
                          className="w-full rounded border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
                          placeholder="Your bid amount (USD)"
                          type="number"
                          value={form.bidAmount || ''}
                          onChange={(e) => setBidForms({ ...bidForms, [deal.id]: { ...form, bidAmount: e.target.value } })}
                        />
                        <input
                          className="w-full rounded border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
                          placeholder="Portfolio URL"
                          value={form.portfolioUrl || ''}
                          onChange={(e) => setBidForms({ ...bidForms, [deal.id]: { ...form, portfolioUrl: e.target.value } })}
                        />
                        <textarea
                          className="w-full rounded border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
                          placeholder="Short note (optional)"
                          value={form.bidNote || ''}
                          onChange={(e) => setBidForms({ ...bidForms, [deal.id]: { ...form, bidNote: e.target.value } })}
                        />
                        <Button size="sm" onClick={() => handleBid(deal.id)} disabled={loading}>Place bid (private)</Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <h2 className="text-xl font-semibold text-white mb-3">Accepted deals & delivery</h2>
              {acceptedDeals.length === 0 && (
                <p className="text-sm text-amber-100/70">No accepted bids yet. Keep bidding!</p>
              )}
              <div className="space-y-3">
                {acceptedDeals.map((bid) => (
                  <div key={bid.id} className="rounded-lg border border-white/10 bg-white/5 p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-semibold">{bid.deal?.title || 'Deal'}</p>
                        <p className="text-xs text-amber-100/70">Bid: ${bid.bid_amount}</p>
                      </div>
                      <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-100 border border-green-500/30">Accepted</span>
                    </div>
                    <UploadRow dealId={bid.deal_id} bidId={bid.id} onSubmit={handleSubmitDelivery} />
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {mode === 'brand' && (
          <div className="space-y-6">
            <section className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <h2 className="text-xl font-semibold text-white mb-3">Post a deal</h2>
              <form className="grid gap-3 md:grid-cols-2" onSubmit={handleCreateDeal}>
                <input className="rounded border border-white/10 bg-white/5 px-3 py-2 text-sm text-white" placeholder="Title" value={dealForm.title} onChange={(e) => setDealForm({ ...dealForm, title: e.target.value })} />
                <input className="rounded border border-white/10 bg-white/5 px-3 py-2 text-sm text-white" placeholder="Deadline (YYYY-MM-DD)" value={dealForm.deadline} onChange={(e) => setDealForm({ ...dealForm, deadline: e.target.value })} />
                <input className="rounded border border-white/10 bg-white/5 px-3 py-2 text-sm text-white" placeholder="Budget min" type="number" value={dealForm.budgetMin} onChange={(e) => setDealForm({ ...dealForm, budgetMin: e.target.value })} />
                <input className="rounded border border-white/10 bg-white/5 px-3 py-2 text-sm text-white" placeholder="Budget max" type="number" value={dealForm.budgetMax} onChange={(e) => setDealForm({ ...dealForm, budgetMax: e.target.value })} />
                <textarea className="rounded border border-white/10 bg-white/5 px-3 py-2 text-sm text-white md:col-span-2" placeholder="Brief" value={dealForm.brief} onChange={(e) => setDealForm({ ...dealForm, brief: e.target.value })} />
                <textarea className="rounded border border-white/10 bg-white/5 px-3 py-2 text-sm text-white md:col-span-2" placeholder="Deliverables" value={dealForm.deliverables} onChange={(e) => setDealForm({ ...dealForm, deliverables: e.target.value })} />
                <div className="md:col-span-2 flex justify-end">
                  <Button type="submit" disabled={loading}>Post deal</Button>
                </div>
              </form>
            </section>

            <section className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xl font-semibold text-white">My deals</h2>
                <Button size="sm" variant="ghost" onClick={loadData} disabled={loading}>Refresh</Button>
              </div>
              {myDeals.length === 0 && <p className="text-sm text-amber-100/70">No deals yet. Post one to start receiving bids.</p>}
              <div className="space-y-3">
                {myDeals.map((deal) => (
                  <div key={deal.id} className="rounded-lg border border-white/10 bg-white/5 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-semibold">{deal.title}</p>
                        <p className="text-xs text-amber-100/70">Budget: {deal.budget_min ? `$${deal.budget_min}` : '?'} - {deal.budget_max ? `$${deal.budget_max}` : '?'}</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => { loadBidsForDeal(deal.id); loadSubmissionsForDeal(deal.id); }}>
                        View bids & submissions
                      </Button>
                    </div>

                    {bidsByDeal[deal.id] && (
                      <div className="space-y-2">
                        <p className="text-sm text-white font-semibold">Bids (private)</p>
                        {bidsByDeal[deal.id].length === 0 && <p className="text-xs text-amber-100/70">No bids yet.</p>}
                        {bidsByDeal[deal.id].map((bid) => (
                          <div key={bid.id} className="rounded border border-white/10 bg-white/5 p-3 flex items-center justify-between">
                            <div>
                              <p className="text-white text-sm font-semibold">${bid.bid_amount}</p>
                              <p className="text-xs text-amber-100/70">{bid.bid_note || 'No note'}</p>
                              {bid.portfolio_url && <a className="text-xs text-amber-200 underline" href={bid.portfolio_url} target="_blank" rel="noreferrer">Portfolio</a>}
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => handleAcceptBid(deal.id, bid)} disabled={loading || bid.status === 'accepted'}>
                                {bid.status === 'accepted' ? 'Accepted' : 'Accept'}
                              </Button>
                              {bid.status !== 'accepted' && (
                                <Button size="sm" variant="ghost" onClick={() => ugcMarketplaceService.rejectBid(bid.id).then(() => loadBidsForDeal(deal.id))} disabled={loading}>Reject</Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {submissionsByDeal[deal.id] && (
                      <div className="space-y-2">
                        <p className="text-sm text-white font-semibold">Submissions</p>
                        {submissionsByDeal[deal.id].length === 0 && <p className="text-xs text-amber-100/70">No submissions yet.</p>}
                        {submissionsByDeal[deal.id].map((sub) => (
                          <div key={sub.id} className="rounded border border-white/10 bg-white/5 p-3 flex items-center justify-between">
                            <div>
                              <p className="text-white text-sm font-semibold">{sub.notes || 'Submission'}</p>
                              <p className="text-xs text-amber-100/70">{sub.status}</p>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={async () => {
                                try {
                                  const signed = await storageService.createSignedUrl(sub.storage_path, 3600);
                                  const link = signed?.signedUrl || storageService.getPublicUrl(sub.storage_path) || '#';
                                  window.open(link, '_blank');
                                } catch (err) {
                                  console.error('download failed', err);
                                }
                              }}
                            >
                              Download
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </main>
    </UGCLayout>
  );
};

const UploadRow = ({ dealId, bidId, onSubmit }) => {
  const [file, setFile] = useState(null);
  const [note, setNote] = useState('');

  return (
    <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
      <input type="file" accept="video/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs text-white" />
      <input
        className="flex-1 rounded border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
        placeholder="Notes for the brand"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <Button size="sm" onClick={() => onSubmit(dealId, bidId, file, note)} disabled={!file}>
        Submit delivery
      </Button>
    </div>
  );
};

export default MarketplacePage;
